/**
 * The invite-card templates offered on the share screen
 * (environment.features.newInviteDesign).
 *
 * Every template is built around the event's own cover photo — the same cover
 * the guest sees again when the gallery opens, so the chat and the album match.
 * A template is the frame and type treatment around that photo; the markup for
 * each lives in invite-share.component.html (`#cardTpl`) and its styles under
 * `.ic-*` in invite-share.component.scss.
 *
 * The card is square because WhatsApp keeps an og:image's aspect ratio, and a
 * square preview gets roughly twice the chat space of a 1.91:1 one. The same
 * square is exported as the link preview (1200×1200 JPEG) and sits on top of
 * the printed QR card.
 *
 * Like the old themes, the card uses literal colours, never the app's tokens:
 * it is a picture of a shared object and must look the same in light and dark
 * mode. The name is Manrope 800 in every template (design system §1.4); the
 * templates differ by frame, colour and size, not face.
 *
 * To add a template: add an entry here, a `*ngSwitchCase` block in `#cardTpl`
 * and an `.ic-<id>` style block. The id is also stored server-side
 * (event_tbl.invite_template, validated by pwa-save-invite-card).
 */

export type CardTemplateId = 'cover' | 'polaroid' | 'editorial' | 'stack';

export interface CardTemplate {
  id: CardTemplateId;
  label: string;
  /** Scales the shared name-size steps (see InviteShareComponent.nameUnits) to this template's face. */
  nameScale: number;
  /** The printed card's QR panel sits under the square card in these colours. */
  panelBackground: string;
  panelColor: string;
}

export const INVITE_CARD_TEMPLATES: CardTemplate[] = [
  // Photo edge to edge, soft fade at the bottom, name and date over it.
  { id: 'cover', label: 'Cover', nameScale: 1, panelBackground: '#0F0E0D', panelColor: '#FFFFFF' },
  // Photo in a tilted instant-print frame, name on the frame. The frame's chin
  // is narrow, so the name runs smaller here (fits a 44-character name in three lines).
  { id: 'polaroid', label: 'Polaroid', nameScale: .75, panelBackground: '#EFDCCB', panelColor: '#3B2A1A' },
  // Photo on top, a cream band below with the name between thin rules.
  { id: 'editorial', label: 'Editorial', nameScale: .92, panelBackground: '#F7F1E7', panelColor: '#2B241C' },
  // The photo as the top print of a small tilted pile, bold name below.
  { id: 'stack', label: 'Stack', nameScale: .9, panelBackground: '#15161D', panelColor: '#FFFFFF' },
];

export const DEFAULT_TEMPLATE_ID: CardTemplateId = 'cover';

export function getCardTemplate(id: CardTemplateId | string | null | undefined): CardTemplate {
  return INVITE_CARD_TEMPLATES.find(t => t.id === id) || INVITE_CARD_TEMPLATES[0];
}
